import { MakeLogicType, connect, events, kea, path, props, selectors } from 'kea'
import { loaders } from 'kea-loaders'

import api from 'lib/api'
import { permanentlyMount } from 'lib/utils/kea-logic-builders'
import { isAuthenticatedTeam, teamLogic } from 'scenes/teamLogic'

import { ActionType } from '~/types'

import type { TeamPublicType, TeamType } from '../types'

export interface ActionsModelProps {
    params?: string
}

export function findActionName(id: number): string | null {
    return actionsModel.findMounted()?.values.actions.find((a) => a.id === id)?.name || null
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface actionsModelValues {
    currentTeam: TeamPublicType | TeamType | null // teamLogic
    actions: ActionType[]
    actionsById: Partial<Record<number | string, ActionType>>
    actionsGrouped: {
        label: string
        options: {
            label: string | null
            value: number
        }[]
    }[]
    actionsLoading: boolean
    actionsSorted: ActionType[]
    pin: any
    pinLoading: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface actionsModelActions {
    loadActions: () => any
    loadActionsFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadActionsSuccess: (
        actions: ActionType[],
        payload?: any
    ) => {
        actions: ActionType[]
        payload?: any
    }
    pinAction: (action: ActionType) => ActionType
    pinActionFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    pinActionSuccess: (
        pin: void,
        payload?: ActionType
    ) => {
        pin: void
        payload?: ActionType
    }
    unpinAction: (action: ActionType) => ActionType
    unpinActionFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    unpinActionSuccess: (
        pin: void,
        payload?: ActionType
    ) => {
        pin: void
        payload?: ActionType
    }
    updateAction: (action: ActionType) => ActionType
    updateActionFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    updateActionSuccess: (
        actions: ActionType[],
        payload?: ActionType
    ) => {
        actions: ActionType[]
        payload?: ActionType
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface actionsModelMeta {
    __keaTypeGenInternalSelectorTypes: {
        actionsGrouped: (actions: ActionType[]) => {
            label: string
            options: {
                label: string | null
                value: number
            }[]
        }[]
        actionsById: (actions: ActionType[]) => Partial<Record<number | string, ActionType>>
        actionsSorted: (actions: ActionType[]) => ActionType[]
    }
}

export type actionsModelType = MakeLogicType<
    actionsModelValues,
    actionsModelActions,
    ActionsModelProps,
    actionsModelMeta
>

export const actionsModel = kea<actionsModelType>([
    props({} as ActionsModelProps),
    path(['models', 'actionsModel']),
    connect(() => ({
        values: [teamLogic, ['currentTeam']],
    })),
    loaders(({ props, values, actions }) => ({
        actions: {
            __default: [] as ActionType[],
            loadActions: async () => {
                const response = await api.actions.list(props.params)
                return response.results ?? []
            },
            updateAction: (action: ActionType) => (values.actions || []).map((a) => (action.id === a.id ? action : a)),
        },
        pin: {
            pinAction: async (action: ActionType) => {
                const response = await api.actions.update(action.id, {
                    name: action.name,
                    pinned_at: new Date().toISOString(),
                })
                actions.updateAction(response)
            },
            unpinAction: async (action: ActionType) => {
                const response = await api.actions.update(action.id, {
                    name: action.name,
                    pinned_at: null,
                })
                actions.updateAction(response)
            },
        },
    })),
    selectors(({ selectors }) => ({
        actionsGrouped: [
            () => [selectors.actions],
            (actions: ActionType[]) => {
                return [
                    {
                        label: 'Select an action',
                        options: actions.map((action) => {
                            return { label: action.name, value: action.id }
                        }),
                    },
                ]
            },
        ],
        actionsById: [
            (s) => [s.actions],
            (actions: ActionType[]): Partial<Record<string | number, ActionType>> =>
                Object.fromEntries(actions.map((action) => [action.id, action])),
        ],
        actionsSorted: [
            (s) => [s.actions],
            (actions: ActionType[]): ActionType[] => {
                return actions.sort((a, b) => (b.pinned_at ? 1 : 0) - (a.pinned_at ? 1 : 0))
            },
        ],
    })),
    events(({ values, actions }) => ({
        afterMount: () => {
            if (isAuthenticatedTeam(values.currentTeam)) {
                // Don't load on shared insights/dashboards
                actions.loadActions()
            }
        },
    })),
    permanentlyMount(),
])
